import { env } from '@/shared/config/env';

import { readJson, throwProblem } from './problem';

/**
 * Запросы к админскому API.
 *
 * Сессия живёт в httpOnly-cookie, поэтому каждый запрос идёт с `credentials: 'include'` —
 * забыть это в одном месте значит получить 401 там, где сотрудник вошёл. Ошибки разбираются
 * одинаково: не-2xx поднимается как `ApiProblemError`, дальше фича ветвится по `problemType`.
 *
 * Путь передаётся без базы — `/v1/admin/attributes`, а не полный адрес.
 */
async function request(method: string, path: string, body?: unknown): Promise<Response> {
  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    await throwProblem(response);
  }
  return response;
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await request('GET', path);
  return (await response.json()) as T;
}

export async function apiSend<T>(method: 'POST' | 'PATCH' | 'PUT', path: string, body: unknown): Promise<T> {
  const response = await request(method, path, body);
  return (await response.json()) as T;
}

/** Удаление и другие ответы без тела: бэкенд отвечает 204. */
export async function apiDelete(path: string): Promise<void> {
  await request('DELETE', path);
}

/**
 * Удаление, отвечающее телом.
 *
 * Так устроено снятие блокировки покупателя: `DELETE .../block` возвращает карточку целиком,
 * потому что удаляется не запись, а состояние — и вызывающей стороне нужен результат, а не
 * подтверждение.
 *
 * `null` — тело не пришло или не разобралось: 204 вместо описанного 200, обрыв прокси. Удаление
 * при этом состоялось, и разбирать такой ответ как сетевой сбой значило бы соврать о результате.
 * Чем заменить недошедшую запись — перечитыванием или ничем — решает вызывающая сторона.
 */
export async function apiDeleteJson<T>(path: string): Promise<T | null> {
  const response = await request('DELETE', path);
  return readJson<T>(response);
}

/**
 * Команда, результат которой не нужен: тело ответа не читается вовсе. Так вызывающая сторона
 * не зависит от того, отдаёт бэкенд запись целиком или отвечает 204 — на пустом теле разбор
 * JSON падал бы, и успешная операция выглядела бы как сетевой сбой.
 */
export async function apiCommand(
  method: 'POST' | 'PATCH' | 'PUT',
  path: string,
  body: unknown,
): Promise<void> {
  await request(method, path, body);
}

/**
 * Списки админки бэкенд по общему правилу заворачивает в `{ items, … }` (курсорная
 * пагинация), но «отдать всё целиком» — дерево категорий, схемы нумерации, справочники —
 * может прийти и голым массивом. Пока типы не сгенерированы из спеки, принимаем обе формы.
 */
export function unwrapItems<T>(body: unknown): T[] {
  if (Array.isArray(body)) {
    return body as T[];
  }
  if (body && typeof body === 'object' && Array.isArray((body as { items?: unknown }).items)) {
    return (body as { items: T[] }).items;
  }
  return [];
}
